import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, FileText, Loader2, X, Share2, Mail } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGS = [
  { code: "it", label: "Italiano" },
  { code: "en", label: "English" },
  { code: "ru", label: "Русский" },
];

const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

const triggerDownload = (lang) => {
  const a = document.createElement("a");
  a.href = `${API}/investor-brochure/${lang}`;
  a.setAttribute("download", `RISORICE-Investor-Brochure-${lang.toUpperCase()}.pdf`);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const InvestorBrochure = () => {
  const { t, lang } = useLang();
  const b = t.simulator.brochure;
  const [registered, setRegistered] = useState(false);
  const [modalLang, setModalLang] = useState(null);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });

  const brochureUrl = `${API}/investor-brochure/${lang}`;
  const waHref = `https://wa.me/?text=${encodeURIComponent(`${b.shareText} ${brochureUrl}`)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent(b.emailSubject)}&body=${encodeURIComponent(`${b.shareText}\n${brochureUrl}`)}`;

  const onLangClick = (code) => {
    if (registered) {
      triggerDownload(code);
      toast.success(b.downloading);
      return;
    }
    setModalLang(code);
    setSending(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !emailValid(form.email.trim()) || form.phone.trim().length < 5) {
      toast.error(b.required);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/brochure-leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        lang: modalLang,
        origin: window.location.origin,
      });
      setRegistered(true);
      triggerDownload(modalLang);
      toast.success(b.downloading);
      setModalLang(null);
    } catch (err) {
      toast.error(b.required);
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="py-16 md:py-24 bg-surface" data-testid="investor-brochure-section">
      <div className="mx-auto max-w-[1000px] px-5 md:px-8">
        <Reveal>
          <div className="rounded-[2rem] bg-ink text-white p-8 md:p-12 overflow-hidden relative">
            <div className="absolute top-0 right-0 h-1.5 w-full bg-tricolor-green" />
            <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-center">
              <div>
                <span className="pill bg-tricolor-green text-white inline-flex items-center gap-2">
                  <FileText size={14} /> {b.badge}
                </span>
                <h2 className="mt-5 font-display font-extrabold text-3xl md:text-4xl leading-tight">{b.title}</h2>
                <p className="mt-4 text-white/60 leading-relaxed max-w-xl">{b.body}</p>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-2 text-sm text-white/50">
                    <Share2 size={16} /> {b.shareLabel}:
                  </span>
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-ink font-semibold px-4 py-2 text-sm hover:opacity-90 transition-opacity"
                    data-testid="brochure-share-whatsapp"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg> {b.shareWhatsapp}
                  </a>
                  <a
                    href={mailHref}
                    className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 font-semibold px-4 py-2 text-sm hover:bg-white/20 transition-colors"
                    data-testid="brochure-share-email"
                  >
                    <Mail size={15} /> {b.shareEmail}
                  </a>
                </div>
              </div>

              <div className="flex flex-col gap-3 lg:min-w-[220px]">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => onLangClick(l.code)}
                    className="flex items-center justify-between gap-4 rounded-xl border border-white/15 bg-white/5 px-5 py-3.5 hover:border-tricolor-green hover:bg-tricolor-green/10 transition-colors duration-200 group text-left"
                    data-testid={`brochure-download-${l.code}`}
                  >
                    <span className="font-semibold">{l.label}</span>
                    <span className="flex items-center gap-2 text-sm text-white/50 group-hover:text-tricolor-green transition-colors">
                      {b.cta} <Download size={16} />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      {modalLang && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-testid="brochure-modal-wrap">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => { if (!sending) setModalLang(null); }} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 md:p-8 shadow-2xl" data-testid="brochure-modal">
            <button type="button" onClick={() => { if (!sending) setModalLang(null); }} className="absolute top-4 right-4 text-muted hover:text-ink transition-colors" data-testid="brochure-modal-close" aria-label="Close">
              <X size={20} />
            </button>
            <h3 className="font-display font-extrabold text-2xl text-ink pr-8">{b.formTitle}</h3>
            <p className="mt-2 text-sm text-muted leading-relaxed">{b.formSubtitle}</p>
            <form onSubmit={submit} className="space-y-4 mt-5">
              <div>
                <label className="block text-sm font-semibold text-ink mb-1.5">{b.name} *</label>
                <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-xl border border-line bg-white px-4 py-2.5 focus:outline-none focus:border-tricolor-green" data-testid="brochure-modal-name" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-ink mb-1.5">{b.email} *</label>
                <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-xl border border-line bg-white px-4 py-2.5 focus:outline-none focus:border-tricolor-green" data-testid="brochure-modal-email" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-ink mb-1.5">{b.phone} *</label>
                <input type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full rounded-xl border border-line bg-white px-4 py-2.5 focus:outline-none focus:border-tricolor-green" data-testid="brochure-modal-phone" />
              </div>
              <button type="submit" disabled={sending} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="brochure-modal-submit">
                {sending ? <><Loader2 size={18} className="animate-spin" /> {b.sending}</> : <><Download size={18} /> {b.submit}</>}
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};
